import { Datalist, Link } from '@ktag/styleguide/dist/react';
import React, { ReactNode, useEffect } from 'react';
import { LayerIdentifyResponse, LayerResult, LayerResultItem } from '../api/contract';
import useAppState from '../../../AppStore';
import Accordion from './Accordion';
import AccordionItem from './AccordionItem';
import './identifyLayerInfoResults.scss';
import { useTracking } from '../../../utils/tracking/TrackingContext';
import ResultsLink from './IdentifyLayerInfoResultsLink';
import { useExternalViewerAPI } from '../../../ExternalViewerApi/ExternalViewerAPIContext';
import { AccordionName } from '../../../UIStateSlice';

const isValidUrl = (url: string): boolean => {
  try {
    const allowedProtocols = ['http:', 'https:', 'ftp:', 'ftps:', 'mailto:', 'tel:'];
    const parsedUrl = new URL(url);
    return allowedProtocols.includes(parsedUrl.protocol);
  } catch {
    return false;
  }
};

const extractLinkData = (htmlString: string) => {
  if (isValidUrl(htmlString)) {
    return {
      url: htmlString,
      text: htmlString,
    };
  }

  const match = htmlString.match(/^<a\b[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/i);
  if (match) {
    return {
      url: match[1],
      text: match[2],
    };
  }
  return null;
};

interface IdentifyLayerResponseProps {
  identifyResponse: LayerIdentifyResponse | undefined;
}

const IdentifyLayerInfoResults = ({
  identifyResponse: layerResults,
}: IdentifyLayerResponseProps) => {
  const setActiveFlyinView = useAppState((state) => state.setActiveFlyinView);
  const setIdentifyLegend = useAppState((state) => state.setIdentifyLegend);
  const accordions = useAppState((state) => state.accordions);
  const setAccordionItem = useAppState((state) => state.setAccordionItem);

  const { Tracking } = useTracking();
  const externalApi = useExternalViewerAPI();

  useEffect(() => {
    if (layerResults?.results) {
      layerResults.results.forEach((layerResult: LayerResult) => {
        const id = `${layerResult.topiclayerId}_${layerResult.layerId}`;
        const exist = !!accordions[AccordionName.IDENTIFY_CONFIGURATION]?.find((i) => {
          return i.id === id;
        });
        if (!exist) setAccordionItem(AccordionName.IDENTIFY_CONFIGURATION, id, true);
      });
    }
  }, [layerResults]);

  if (!layerResults) {
    return null;
  }

  const showLegendClicked = (layerResult: LayerResult) => {
    Tracking.Identify.showLegend(
      layerResult.topiclayerId,
      layerResult.layerId,
      layerResult.layerTitle,
    );
    setIdentifyLegend({
      topicLayerId: layerResult.topiclayerId,
      layerId: layerResult.layerId,
      title: layerResult.layerTitle,
    });
    setActiveFlyinView('identify_legende');
  };

  const onLinkWithJSEventClicked = (
    item: LayerResultItem,
    layerId: string,
    subLayerTitle: string,
    subLayerId: number,
  ) => {
    externalApi?.onObjectSelected?.({
      layerId,
      subLayerTitle,
      subLayerId,
      attributes: item.attributes,
    });
  };

  const formatDateString = (value: unknown): string | ReactNode => {
    if (typeof value !== 'string') return String(value);

    const link = extractLinkData(value);
    if (link) {
      return <Link text={link.text} url={link.url} openNewWindow />;
    }

    if (!/^\d{4}-\d{2}-\d{2}/.test(value)) return value;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  };

  const formatKey = (key: string): string => {
    if (!key) return key;
    return key.replace(/([_.])/g, '$1\u200B');
  };

  return (
    <div className="LayerResultComponent">
      <Accordion>
        {layerResults?.results?.map((layerResult: LayerResult) => (
          <AccordionItem
            key={`${layerResult.topiclayerId}_${layerResult.layerId}`}
            id={`${layerResult.topiclayerId}_${layerResult.layerId}`}
            title={layerResult.layerTitle}
            onToggle={(state) => {
              Tracking.Identify.accordionToggle(
                layerResult.topiclayerId,
                state ? 'closed' : 'open',
              );
            }}
          >
            {/* eslint-disable jsx-a11y/anchor-is-valid */}
            <a
              className="form__actionslink"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                showLegendClicked(layerResult);
              }}
              role="button"
            >
              Legende
            </a>
            {layerResult.items.map((item) => {
              const attributeItems = Object.values(item.attributes).map(
                ({ value, displayName, name }) => ({
                  key: formatKey(displayName ?? name),
                  value: formatDateString(value),
                }),
              );

              const linkItems = item.links.map((link) => ({
                key: 'Link',
                value: (
                  <ResultsLink
                    description={link.description}
                    link={link.link}
                    onLinkWithJSEventClicked={() =>
                      onLinkWithJSEventClicked(
                        item,
                        layerResult.topiclayerId,
                        layerResult.layerTitle,
                        layerResult.layerId,
                      )
                    }
                  />
                ),
              }));
              return (
                <div className="featureInfoContainer" key={item.title}>
                  <Datalist items={[...attributeItems, ...linkItems]} />
                </div>
              );
            })}
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

export default IdentifyLayerInfoResults;
